import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { FormLabel, Paper, Typography } from "@mui/material";
import { TurningRadiusLimitsViewModel } from "portal/utils/atkEquipment";
import { TypedPathWrapper } from "typed-path";
import { useRandomId } from "portal/utils/hooks/useRandomId";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import React from "react";

interface TurningRadiusLimitsSubformProps<
  TBasePath extends TypedPathWrapper<TurningRadiusLimitsViewModel, any>
> {
  values: TurningRadiusLimitsViewModel;
  basePath: TBasePath;
  disabled?: boolean;
}

export const TurningRadiusLimitsSubform = <
  TBasePath extends TypedPathWrapper<TurningRadiusLimitsViewModel, any>
>({
  values,
  basePath,
  disabled = false,
}: TurningRadiusLimitsSubformProps<TBasePath>): JSX.Element => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const fullyRaisedId = useRandomId();
  const partlyLoweredId = useRandomId();
  const mostlyLoweredId = useRandomId();
  const fullyLoweredId = useRandomId();

  return (
    <Paper
      variant="outlined"
      className="lg:w-1/2 bg-inherit flex flex-col gap-2 p-4 rounded-lg"
    >
      <Typography>
        {t(
          "views.autotractor.equipment.implements.turningRadius.sectionHeader"
        )}
      </Typography>
      <Typography color="textSecondary">
        <InfoIcon fontSize="small" className="align-middle mr-1" />
        {t("views.autotractor.equipment.implements.turningRadius.note")}
      </Typography>
      <div className="grid grid-cols-2 gap-2 items-center">
        <FormLabel id={fullyRaisedId}>
          {t(
            "views.autotractor.equipment.implements.turningRadius.75_100percent"
          )}
        </FormLabel>
        <FormikDimensionedNumberInput
          name={basePath.radius75to100M.$path}
          label=""
          dimensionedNumberInputProps={{
            disabled,
            storedUnit: "m",
            displayUnits: { imperial: "ft", metric: "m" },
            localSystem: measurementSystem,
            minStoredValue: 0,
            textFieldProps: {
              "aria-labelledby": fullyRaisedId,
            },
          }}
          className="w-full"
        />

        <FormLabel id={partlyLoweredId}>
          {t(
            "views.autotractor.equipment.implements.turningRadius.50_75percent"
          )}
        </FormLabel>
        <FormikDimensionedNumberInput
          name={basePath.radius50to75M.$path}
          label=""
          dimensionedNumberInputProps={{
            disabled,
            storedUnit: "m",
            displayUnits: { imperial: "ft", metric: "m" },
            localSystem: measurementSystem,
            minStoredValue: values.radius75to100M,
            textFieldProps: {
              "aria-labelledby": partlyLoweredId,
            },
          }}
          className="w-full"
        />

        <FormLabel id={mostlyLoweredId}>
          {t(
            "views.autotractor.equipment.implements.turningRadius.25_50percent"
          )}
        </FormLabel>
        <FormikDimensionedNumberInput
          name={basePath.radius25to50M.$path}
          label=""
          dimensionedNumberInputProps={{
            disabled,
            storedUnit: "m",
            displayUnits: { imperial: "ft", metric: "m" },
            localSystem: measurementSystem,
            minStoredValue: values.radius50to75M,
            textFieldProps: {
              "aria-labelledby": mostlyLoweredId,
            },
          }}
          className="w-full"
        />

        <FormLabel id={fullyLoweredId}>
          {t(
            "views.autotractor.equipment.implements.turningRadius.0_25percent"
          )}
        </FormLabel>
        <FormikDimensionedNumberInput
          name={basePath.radius0to25M.$path}
          label=""
          dimensionedNumberInputProps={{
            disabled,
            storedUnit: "m",
            displayUnits: { imperial: "ft", metric: "m" },
            localSystem: measurementSystem,
            minStoredValue: values.radius25to50M,
            textFieldProps: {
              "aria-labelledby": fullyLoweredId,
            },
          }}
          className="w-full"
        />
      </div>
    </Paper>
  );
};
